import React from "react";
import { errorText, fetchWithAuth } from "../utils/authFetch";
import { readSseStream } from "../utils/readSseStream";

/* ─────────────────────────────────────────────────────────────────────────────
   The filter assistant behind the working sheet. Renders nothing: the sheet owns
   the transcript and the plan, and drives this through its ref. One turn = one
   streamed request to the editor, which applies a message to the plan (or, for a
   past search, introduces it). Steps stream back through `onActivity`, the result
   lands through `onRecommendation`.
   ───────────────────────────────────────────────────────────────────────────── */

const FilterAssistant = React.forwardRef(function FilterAssistant(
  {
    brief,
    searchId,
    title,
    description,
    countries,
    countryCodes,
    worldwide,
    stats,
    userSub,
    /* "intro" = on mount, read the search and offer suggested edits without changing it
       (a past search); false = wait for a message (a new search). */
    autoRun = false,
    onBusyChange,
    onRecommendation,
    /* Live steps for the sheet's rail: {id, label, args, state, detail}. */
    onActivity,
  },
  ref,
) {
  // The conversation so far, sent back with every turn so the editor has context.
  const historyRef = React.useRef([]);
  const abortRef = React.useRef(null);
  const busyRef = React.useRef(false);
  // Always the latest props at the moment a turn starts (the page edits the brief freely).
  const propsRef = React.useRef({});
  propsRef.current = {
    brief,
    searchId,
    title,
    description,
    countries,
    countryCodes,
    worldwide,
    stats,
    userSub,
    onRecommendation,
    onActivity,
  };

  const setBusy = React.useCallback(
    (v) => {
      busyRef.current = v;
      if (onBusyChange) onBusyChange(v);
    },
    [onBusyChange],
  );

  const runTurn = React.useCallback(
    async (message, opts = {}) => {
      if (busyRef.current) return;
      const p = propsRef.current;
      if (!p.brief) return;
      const text = String(message || "").trim();
      const mode = opts.mode || (text ? "edit" : "intro");
      const before = p.brief;
      if (text) historyRef.current.push({ role: "user", content: text });
      setBusy(true);
      const controller = new AbortController();
      abortRef.current = controller;
      let doneEvent = null;
      try {
        const res = await fetchWithAuth("/api/people-search/agent-stream", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          signal: controller.signal,
          body: JSON.stringify({
            sub: p.userSub,
            brief: p.brief,
            message: text,
            mode,
            history: historyRef.current.slice(-12),
            title: p.title || "",
            description: p.description || "",
            countries: p.worldwide ? [] : p.countries || [],
            country_codes: p.worldwide
              ? []
              : (p.countryCodes || []).map((c) => String(c || "").toLowerCase()),
            worldwide: !!p.worldwide,
            stats: p.stats || undefined,
            // Ties this turn's credit charge to the search it is setting up.
            search_id: p.searchId || undefined,
          }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(
            body?.error ||
              "The assistant is unavailable right now. Please try again.",
          );
        }
        await readSseStream(res, (ev) => {
          if (ev.type === "tool") {
            p.onActivity?.({
              id: ev.id,
              label: ev.label,
              args: ev.args_summary || "",
              state: "running",
              detail: "working…",
            });
          } else if (ev.type === "tool_result") {
            p.onActivity?.({
              id: ev.id,
              state: ev.ok ? "done" : "failed",
              detail: ev.summary?.error ? "didn't work" : "",
            });
          } else if (ev.type === "done") {
            doneEvent = ev;
          } else if (ev.type === "error") {
            throw new Error(
              ev.message || "The assistant hit a problem. Please try again.",
            );
          }
        });
        if (!doneEvent)
          throw new Error(
            "The assistant stopped before finishing. Please try again.",
          );
        if (doneEvent.reply)
          historyRef.current.push({ role: "assistant", content: doneEvent.reply });
        p.onRecommendation?.(doneEvent, before);
      } catch (err) {
        // A turn the user (or navigation) cut short has nothing to show; any other
        // failure lands as a reply with the plan unchanged.
        if (err?.name !== "AbortError")
          p.onRecommendation?.(
            {
              reply: errorText(err, "The assistant hit a problem. Please try again."),
              brief: before,
              changed: false,
              changes: [],
              forced: true,
            },
            before,
          );
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
        setBusy(false);
      }
    },
    [setBusy],
  );

  const stop = React.useCallback(() => {
    abortRef.current?.abort();
  }, []);

  React.useImperativeHandle(
    ref,
    () => ({ runTurn, stop, busy: () => busyRef.current }),
    [runTurn, stop],
  );

  /* Intro turn on mount for a past search. Unmounting aborts whatever is in flight. */
  React.useEffect(() => {
    if (autoRun) runTurn("", { mode: String(autoRun) });
    return () => {
      abortRef.current?.abort();
    };
  }, []); // mount only, on purpose

  return null;
});

export default FilterAssistant;
